import type {
  CommittedDelta,
  DeltaTarget,
  Operation,
  OperationType,
  ProposedDelta,
  UserDelta,
} from "./deltas";
import type { RoundOperation } from "./narrative";
import type { Fork, PerspectiveDestination, ViewRef } from "./perspectives";
import { reachedViews, viewKey } from "./targets";
import type { Arrangement, Thing, ThingKind } from "./things";
import { speakerKey, type ThreadSpeaker, type ThreadStatus } from "./threads";
import type { Input, Label, Scene, TimelineExtension } from "./timeline";

/**
 * A thing on a view's map: what kind it is, the canvas it sits on, whether it has a name, where a
 * text or card is, a canvas's styles, and a board's arrangement.
 */
export interface ThingValidationEntry {
  kind: ThingKind;
  canvas?: string;
  named?: boolean;
  on?: "at" | "point" | "line" | "along" | "across";
  styles?: string[];
  arrangement?: Arrangement;
}

/** Which way a text or card stands: at a place, or on a point, a line, along one, or across ground. */
export const onKind = (on: Record<string, unknown>): NonNullable<ThingValidationEntry["on"]> =>
  "at" in on
    ? "at"
    : "point" in on
      ? "point"
      : "line" in on
        ? "line"
        : "along" in on
          ? "along"
          : "across";

export function validationEntryOf(thing: Thing): ThingValidationEntry {
  return {
    kind: thing.kind,
    ...("canvas" in thing ? { canvas: thing.canvas } : {}),
    ...("name" in thing && thing.name !== undefined ? { named: true } : {}),
    ...(thing.kind === "text" || thing.kind === "card" ? { on: onKind(thing.on) } : {}),
    ...(thing.kind === "canvas" && thing.styles ? { styles: Object.keys(thing.styles) } : {}),
    ...(thing.kind === "canvas" && thing.arrangement ? { arrangement: thing.arrangement } : {}),
  };
}

/** A view's place in the story, set by the round deltas it has received. */
export interface Clock {
  /** The current round. During a jump, the round the jump ends. */
  round: number;
  jumping: boolean;
  /** The tick the current round began at. */
  tick: number;
}

/** What the spectator and every perspective share: the map, and the story so far. */
export interface ViewValidationIndex {
  /** Every thing on the map, canvases included, by id. */
  things: Map<string, ThingValidationEntry>;
  /** The handle of each perspective the view has an alias for. */
  aliases: Set<string>;
  clock: Clock | null;
  /** The ids of the scenes the view has been shown, and of their events. */
  sceneIds: Set<string>;
  eventIds: Set<string>;
  /** The tick the view's timeline ends at. */
  end: number;
  /** The date labelled at each tick. */
  labels: Map<number, string>;
}

export interface SpectatorValidationIndex extends ViewValidationIndex {
  /** Each perspective suggestion, with the canvas of its starting view. */
  perspectiveSuggestions: Map<string, { startingViewCanvasId: string }>;
}

export interface PollValidationEntry {
  status: "open" | "closed";
  optionIds: Set<string>;
  freeform: boolean;
}

export interface ThreadValidationEntry {
  status: ThreadStatus;
  /** Each member, listed or removed. */
  members: Map<string, { listed: boolean }>;
  entryIds: Set<string>;
  /** Entries that are messages, from the perspective or a member, and so can be reacted to. */
  messageIds: Set<string>;
  polls: Map<string, PollValidationEntry>;
  /** Every reaction there is, by `reactionKey`. */
  reactions: Set<string>;
}

/** One key per mark left by one participant on one message. */
export function reactionKey(entryId: string, mark: string, reactor: ThreadSpeaker): string {
  return `${entryId} ${mark} ${speakerKey(reactor)}`;
}

export interface ActionValidationEntry {
  status: "pending" | "committed" | "withdrawn";
}

export interface PerspectiveRecord {
  userIds: Set<string>;
  handle: string;
  startingViewCanvasId?: string;
}

export interface PerspectiveValidationIndex extends ViewValidationIndex {
  /** Null until its `perspective.set`, since a fork's copies come before it. */
  perspective: PerspectiveRecord | null;
  /** Its ask for the next jump. A jump starting, or its last user leaving, drops it. */
  jumpRequest: { guidance?: string } | null;
  /** The input it was last asked, until it is answered or withdrawn. */
  awaits: Input | null;
  threads: Map<string, ThreadValidationEntry>;
  actions: Map<string, ActionValidationEntry>;
}

export interface UserValidationIndex {
  advisorEntryIds: Set<string>;
  /** Every ask to change perspective the user has made. */
  perspectiveChangeIds: Set<string>;
  /** The user's unanswered ask to change perspective, and whether it is to spectate or to play. */
  perspectiveChange: { id: string; to: PerspectiveDestination["type"] } | null;
}

/** A round boundary: the round completed there, and the tick the next round begins at. */
export interface RoundBoundary {
  roundCompleted: number;
  tick: number;
}

/** What only whole-record validation reads. It never changes a view or a user. */
export interface WholeRecordIndex {
  /** Every handle ever set, including those of perspectives since unset. */
  handles: Set<string>;
  /** User id to the handle of the perspective they're on. */
  userPerspectives: Map<string, string>;
  /** The perspectives on each distribution list, by list id. */
  distributionLists: Map<string, Set<string>>;
  /** The spectator's round boundaries, in order. */
  boundaries: RoundBoundary[];
  /** The operations each view has that a fork copies, in order, by view key. */
  forkCopies: Map<string, Operation[]>;
  skills: Set<string>;
  /** The agent holding the game master role, or null while nobody does. */
  gameMaster: { agentId: string; agentVersionId: string } | null;
  /** Every agent that has held the role. */
  gameMasterAgentIds: Set<string>;
}

export interface ValidationIndexes {
  spectator: SpectatorValidationIndex;
  perspectives: Map<string, PerspectiveValidationIndex>;
  users: Map<string, UserValidationIndex>;
  wholeRecord: WholeRecordIndex;
  /**
   * The record these were built from, which the caller appends committed deltas to. A rejected
   * batch validated with `mutate` rebuilds the indexes from it.
   */
  record: readonly CommittedDelta[] | readonly UserDelta[];
  /** The user whose copy of the record these come from, when they come from a copy. */
  copyFor?: ProposingUser;
}

/** The user proposing, and the perspective they are on. No perspective means the spectator. */
export interface ProposingUser {
  userId: string;
  perspectiveHandle?: string;
}

/** Operations that reach a user rather than a view. */
export const USER_OPERATION_TYPES: ReadonlySet<OperationType> = new Set([
  "advisor.entryAdded",
  "advisor.cleared",
  "perspectiveChange.requested",
  "perspectiveChange.withdrawn",
  "perspectiveChange.approved",
  "perspectiveChange.refused",
]);

/** Operations that reach the game master alone, and so land in no view and no user. */
const SERVER_OPERATION_PREFIXES = ["distributionList.", "skill.", "gameMaster."];

const ROUND_OPERATION_TYPES: ReadonlySet<OperationType> = new Set([
  "world.started",
  "jump.started",
  "jump.completed",
]);

export function isRoundOperation(operation: Operation): operation is RoundOperation {
  return ROUND_OPERATION_TYPES.has(operation.type);
}

/** Writes on the timeline, scenes shown, aliases, and round operations: the ones a fork copies. */
export function isForkCopied(type: OperationType): boolean {
  return (
    type === "timeline.extended" ||
    type === "timeline.clarified" ||
    type === "scene.shown" ||
    type.startsWith("alias.") ||
    ROUND_OPERATION_TYPES.has(type)
  );
}

export function isPerspectiveView(view: ViewValidationIndex): view is PerspectiveValidationIndex {
  return "threads" in view;
}

export function isSpectatorView(view: ViewValidationIndex): view is SpectatorValidationIndex {
  return "perspectiveSuggestions" in view;
}

/** Whether a perspective has users, and so receives the world's round deltas. */
export function hasUsers(view: PerspectiveValidationIndex): boolean {
  return (view.perspective?.userIds.size ?? 0) > 0;
}

/** Whom a delta reached: its target, or for a delta in a user's copy, that user or their view. */
export function targetOf(delta: ProposedDelta | UserDelta, user?: ProposingUser): DeltaTarget {
  return "target" in delta ? delta.target : userTarget(delta.operation, user);
}

/** Where an operation in a user's copy reached them: their user, or their own view. */
export function userTarget(operation: Operation, user: ProposingUser | undefined): DeltaTarget {
  if (!user) throw new Error("A user's copy of the record needs the user it belongs to.");
  if (USER_OPERATION_TYPES.has(operation.type)) return { type: "users", userIds: [user.userId] };
  return { type: "perspectives", perspectives: [ownViewRef(user)] };
}

function ownViewRef(user: ProposingUser): ViewRef {
  return user.perspectiveHandle
    ? { type: "perspective", handle: user.perspectiveHandle }
    : { type: "spectator" };
}

/** Indexes for an empty world, to build from `record`. A user's copy starts with only their view. */
export function emptyIndexes(
  record: readonly CommittedDelta[] | readonly UserDelta[],
  user?: ProposingUser,
): ValidationIndexes {
  const indexes: ValidationIndexes = {
    spectator: { ...emptyView(), perspectiveSuggestions: new Map() },
    perspectives: new Map(),
    users: new Map(),
    wholeRecord: {
      handles: new Set(),
      userPerspectives: new Map(),
      distributionLists: new Map(),
      boundaries: [],
      forkCopies: new Map([[viewKey({ type: "spectator" }), []]]),
      skills: new Set(),
      gameMaster: null,
      gameMasterAgentIds: new Set(),
    },
    record,
    ...(user ? { copyFor: user } : {}),
  };
  if (user?.perspectiveHandle) indexes.perspectives.set(user.perspectiveHandle, emptyPerspective());
  return indexes;
}

function emptyView(): ViewValidationIndex {
  return {
    things: new Map(),
    aliases: new Set(),
    clock: null,
    sceneIds: new Set(),
    eventIds: new Set(),
    end: 0,
    labels: new Map(),
  };
}

/** A perspective's view before anything has reached it. */
export function emptyPerspective(): PerspectiveValidationIndex {
  return {
    ...emptyView(),
    perspective: null,
    jumpRequest: null,
    awaits: null,
    threads: new Map(),
    actions: new Map(),
  };
}

export function cloneIndexes(indexes: ValidationIndexes): ValidationIndexes {
  const { spectator, perspectives, users, wholeRecord, record, copyFor } = indexes;
  const { forkCopies, ...rest } = wholeRecord;
  return {
    spectator: copyView(spectator),
    perspectives: new Map([...perspectives].map(([id, view]) => [id, copyView(view)])),
    users: structuredClone(users),
    wholeRecord: {
      ...structuredClone(rest),
      forkCopies: new Map([...forkCopies].map(([key, operations]) => [key, [...operations]])),
    },
    record,
    ...(copyFor ? { copyFor } : {}),
  };
}

function copyView<View extends ViewValidationIndex>(view: View): View {
  return structuredClone(view);
}

function userIndex(indexes: ValidationIndexes, userId: string): UserValidationIndex {
  const existing = indexes.users.get(userId);
  if (existing) return existing;
  const created: UserValidationIndex = {
    advisorEntryIds: new Set(),
    perspectiveChangeIds: new Set(),
    perspectiveChange: null,
  };
  indexes.users.set(userId, created);
  return created;
}

/** The view a ref addresses, if it exists. */
export function viewFor(indexes: ValidationIndexes, ref: ViewRef): ViewValidationIndex | undefined {
  return ref.type === "spectator" ? indexes.spectator : indexes.perspectives.get(ref.handle);
}

/**
 * Every view a target reaches now, each once: distribution lists expanded, and the active views as
 * the spectator and every perspective with users.
 */
export function reachedViewRefs(indexes: ValidationIndexes, target: DeltaTarget): ViewRef[] {
  if (target.type === "activeViews") {
    return [
      { type: "spectator" },
      ...[...indexes.perspectives]
        .filter(([, view]) => hasUsers(view))
        .map(([handle]): ViewRef => ({ type: "perspective", handle })),
    ];
  }
  if (target.type !== "perspectives") return [];
  return reachedViews(target.perspectives, listId =>
    indexes.wholeRecord.distributionLists.get(listId),
  );
}

/** The operations a fork of a view starts with, in the order its users receive them. */
export function forkCopiesOf(indexes: ValidationIndexes, fork: Fork | undefined): Operation[] {
  if (!fork) return [];
  return [...(indexes.wholeRecord.forkCopies.get(viewKey(fork)) ?? [])];
}

/**
 * The copies an operation lands in: each view its target reaches, or each user it targets. In a
 * user's copy, it lands in their own view or their own user, whatever the target.
 */
export interface Landing {
  refs: ViewRef[];
  views: ViewValidationIndex[];
  users: UserValidationIndex[];
  /** For a `perspective.set`, the new perspective's view, with its fork's copies replayed in. */
  created?: { handle: string; view: PerspectiveValidationIndex; copies: Operation[] };
}

/**
 * Where a delta lands. A new perspective's view is built by replaying its fork's copies with
 * `replay`, exactly as its users receive them.
 */
export function landingOf(
  indexes: ValidationIndexes,
  target: DeltaTarget,
  operation: Operation,
  replay: (view: PerspectiveValidationIndex, operation: Operation) => void = applyToView,
): Landing {
  const { copyFor } = indexes;
  if (SERVER_OPERATION_PREFIXES.some(prefix => operation.type.startsWith(prefix))) {
    return { refs: [], views: [], users: [] };
  }
  if (USER_OPERATION_TYPES.has(operation.type)) {
    const userIds = copyFor ? [copyFor.userId] : target.type === "users" ? target.userIds : [];
    return { refs: [], views: [], users: userIds.map(userId => userIndex(indexes, userId)) };
  }
  if (copyFor) {
    const ref = ownViewRef(copyFor);
    const view = viewFor(indexes, ref);
    return { refs: [ref], views: view ? [view] : [], users: [] };
  }
  if (operation.type === "perspective.set") {
    const { handle } = operation.perspective;
    const copies = forkCopiesOf(indexes, operation.fork);
    const view = emptyPerspective();
    for (const copy of copies) replay(view, copy);
    return {
      refs: [{ type: "perspective", handle }],
      views: [view],
      users: [],
      created: { handle, view, copies },
    };
  }
  const refs = reachedViewRefs(indexes, target);
  return {
    refs,
    views: refs.flatMap(ref => viewFor(indexes, ref) ?? []),
    users: [],
  };
}

/** Applies an accepted delta where it lands, without checking it. */
export function applyToIndexes(
  indexes: ValidationIndexes,
  delta: Pick<CommittedDelta, "target" | "operation">,
): ViewRef[] {
  const landing = landingOf(indexes, delta.target, delta.operation);
  commit(indexes, delta.operation, landing);
  return landing.refs;
}

/** Applies an operation to what only the whole record holds, then to every view and user it lands in. */
export function commit(indexes: ValidationIndexes, operation: Operation, landing: Landing): void {
  if (!indexes.copyFor) applyToWholeRecord(indexes, operation, landing);
  for (const view of landing.views) applyToView(view, operation);
  for (const user of landing.users) applyToUser(user, operation);
}

/** Keeps what only whole-record validation reads. Reads views, and never changes one. */
function applyToWholeRecord(
  indexes: ValidationIndexes,
  operation: Operation,
  landing: Landing,
): void {
  const { wholeRecord } = indexes;
  if (landing.created) {
    const { handle, view, copies } = landing.created;
    indexes.perspectives.set(handle, view);
    wholeRecord.forkCopies.set(viewKey({ type: "perspective", handle }), copies);
  }
  if (isForkCopied(operation.type)) {
    for (const ref of landing.refs) wholeRecord.forkCopies.get(viewKey(ref))?.push(operation);
  }
  const reachesSpectator = landing.refs.some(ref => ref.type === "spectator");
  if (
    reachesSpectator &&
    (operation.type === "world.started" || operation.type === "jump.completed")
  ) {
    wholeRecord.boundaries.push({ roundCompleted: operation.roundCompleted, tick: operation.tick });
  }

  switch (operation.type) {
    case "perspective.set":
      wholeRecord.handles.add(operation.perspective.handle);
      return;
    case "perspective.updateUsers":
      for (const ref of landing.refs) {
        if (ref.type !== "perspective") continue;
        for (const [userId, handle] of wholeRecord.userPerspectives) {
          if (handle === ref.handle) wholeRecord.userPerspectives.delete(userId);
        }
        for (const userId of operation.userIds)
          wholeRecord.userPerspectives.set(userId, ref.handle);
      }
      return;
    case "perspective.unset":
      for (const ref of landing.refs) {
        if (ref.type !== "perspective") continue;
        for (const [userId, handle] of wholeRecord.userPerspectives) {
          if (handle === ref.handle) wholeRecord.userPerspectives.delete(userId);
        }
        indexes.perspectives.delete(ref.handle);
        wholeRecord.forkCopies.delete(viewKey(ref));
        for (const members of wholeRecord.distributionLists.values()) members.delete(ref.handle);
      }
      return;
    case "distributionList.set":
      wholeRecord.distributionLists.set(
        operation.distributionList.id,
        new Set(operation.distributionList.handles),
      );
      return;
    case "distributionList.update":
      if (operation.changes.handles && wholeRecord.distributionLists.has(operation.id)) {
        wholeRecord.distributionLists.set(operation.id, new Set(operation.changes.handles));
      }
      return;
    case "distributionList.perspectiveAdded":
      wholeRecord.distributionLists.get(operation.id)?.add(operation.handle);
      return;
    case "distributionList.perspectiveRemoved":
      wholeRecord.distributionLists.get(operation.id)?.delete(operation.handle);
      return;
    case "distributionList.unset":
      wholeRecord.distributionLists.delete(operation.id);
      return;
    case "skill.set":
      wholeRecord.skills.add(operation.skill.id);
      return;
    case "skill.unset":
      wholeRecord.skills.delete(operation.id);
      return;
    case "gameMaster.assigned":
      wholeRecord.gameMaster = {
        agentId: operation.agentId,
        agentVersionId: operation.agentVersionId,
      };
      wholeRecord.gameMasterAgentIds.add(operation.agentId);
      return;
    case "gameMaster.unassigned":
      wholeRecord.gameMaster = null;
      return;
  }
}

/** Applies an operation to one view. A view changes only here, from the operations it receives. */
export function applyToView(view: ViewValidationIndex, operation: Operation): void {
  if (isRoundOperation(operation)) applyClock(view, operation);
  const perspective = isPerspectiveView(view) ? view : null;

  switch (operation.type) {
    case "perspective.set": {
      if (!perspective) return;
      const { handle, startingView: opening } = operation.perspective;
      perspective.perspective = {
        userIds: new Set(),
        handle,
        ...(opening ? { startingViewCanvasId: opening.canvasId } : {}),
      };
      return;
    }
    case "perspective.update": {
      const record = perspective?.perspective;
      if (!record) return;
      const { startingView: opening } = operation.changes;
      if (opening !== undefined) record.startingViewCanvasId = opening?.canvasId;
      return;
    }
    case "perspective.updateUsers": {
      if (!perspective?.perspective) return;
      perspective.perspective.userIds = new Set(operation.userIds);
      if (operation.userIds.length === 0) perspective.jumpRequest = null;
      return;
    }
    case "perspective.unset":
      if (perspective) perspective.perspective = null;
      return;
    case "perspectiveSuggestion.set": {
      if (!isSpectatorView(view)) return;
      const { id, startingView } = operation.perspectiveSuggestion;
      view.perspectiveSuggestions.set(id, { startingViewCanvasId: startingView.canvasId });
      return;
    }
    case "perspectiveSuggestion.update": {
      const suggestion = isSpectatorView(view)
        ? view.perspectiveSuggestions.get(operation.id)
        : null;
      if (!suggestion) return;
      const { startingView } = operation.changes;
      if (startingView) suggestion.startingViewCanvasId = startingView.canvasId;
      return;
    }
    case "perspectiveSuggestion.unset":
      if (isSpectatorView(view)) view.perspectiveSuggestions.delete(operation.id);
      return;
    case "alias.set":
      view.aliases.add(operation.handle);
      return;
    case "alias.unset":
      view.aliases.delete(operation.handle);
      return;
    case "timeline.extended":
      applyWrite(view.things, operation.extension);
      applyLabels(view.labels, operation.extension.labels);
      view.end = operation.extension.end;
      return;
    case "timeline.clarified":
      applyWrite(view.things, operation.clarification);
      applyLabels(view.labels, operation.clarification.labels);
      return;
    case "scene.shown":
      shown(view, operation.scene);
      return;
  }
  if (perspective) applyToPerspective(perspective, operation);
}

function applyLabels(labels: Map<number, string>, written: readonly Label[] | undefined): void {
  for (const label of written ?? []) labels.set(label.tick, label.date);
}

/** What only a perspective's view holds: the input it waits on, its threads, and its actions. */
function applyToPerspective(perspective: PerspectiveValidationIndex, operation: Operation): void {
  switch (operation.type) {
    case "input.asked":
      perspective.awaits = operation.input;
      return;
    case "input.answered":
    case "input.withdrawn":
      perspective.awaits = null;
      return;
    case "jump.requested": {
      const { type: _, ...request } = operation;
      perspective.jumpRequest = request;
      return;
    }
    case "jump.requestWithdrawn":
      perspective.jumpRequest = null;
      return;
    case "jump.started":
      perspective.awaits = null;
      perspective.jumpRequest = null;
      for (const action of perspective.actions.values()) {
        if (action.status === "pending") action.status = "committed";
      }
      return;
    case "jump.completed":
      perspective.awaits = null;
      return;
    case "thread.create":
      perspective.threads.set(operation.thread.id, {
        status: "open",
        members: new Map(operation.thread.members.map(member => [member.id, { listed: true }])),
        entryIds: new Set(),
        messageIds: new Set(),
        polls: new Map(),
        reactions: new Set(),
      });
      return;
    case "thread.append": {
      const thread = perspective.threads.get(operation.threadId);
      if (!thread) return;
      thread.entryIds.add(operation.entry.id);
      const { content, presentation, scene } = operation.entry;
      if (scene) shown(perspective, scene);
      if (content.type === "text" && presentation.type !== "system") {
        thread.messageIds.add(operation.entry.id);
      }
      if (content.type === "poll") {
        thread.polls.set(content.pollId, {
          status: "open",
          optionIds: new Set(content.options.map(option => option.id)),
          freeform: content.freeform === true,
        });
      }
      if (
        presentation.type === "perspective" &&
        perspective.awaits?.type === "reply" &&
        perspective.awaits.threadId === operation.threadId
      ) {
        perspective.awaits = null;
      }
      return;
    }
    case "thread.update": {
      const thread = perspective.threads.get(operation.id);
      if (!thread) return;
      const { changes } = operation;
      if (changes.status) thread.status = changes.status;
      for (const change of changes.members ?? []) {
        if (change.type === "append") {
          thread.members.set(change.member.id, { listed: true });
          continue;
        }
        const member = thread.members.get(change.id);
        if (!member) continue;
        if (change.type === "remove") member.listed = false;
      }
      return;
    }
    case "thread.pollClosed": {
      const poll = perspective.threads.get(operation.threadId)?.polls.get(operation.pollId);
      if (poll) poll.status = "closed";
      return;
    }
    case "thread.reactionSet":
    case "thread.reactionUnset": {
      const reactions = perspective.threads.get(operation.threadId)?.reactions;
      const key = reactionKey(operation.entryId, operation.mark, operation.reactor);
      if (operation.type === "thread.reactionSet") reactions?.add(key);
      else reactions?.delete(key);
      return;
    }
    case "action.submitted":
      perspective.actions.set(operation.action.id, { status: "pending" });
      return;
    case "action.withdrawn": {
      const action = perspective.actions.get(operation.id);
      if (action) action.status = "withdrawn";
      return;
    }
  }
}

/** Applies an operation to one user. A user changes only here, from the operations they receive. */
export function applyToUser(user: UserValidationIndex, operation: Operation): void {
  switch (operation.type) {
    case "perspectiveChange.requested":
      user.perspectiveChangeIds.add(operation.id);
      user.perspectiveChange = { id: operation.id, to: operation.to.type };
      return;
    case "perspectiveChange.withdrawn":
    case "perspectiveChange.approved":
    case "perspectiveChange.refused":
      if (user.perspectiveChange?.id === operation.id) user.perspectiveChange = null;
      return;
    case "advisor.entryAdded":
      user.advisorEntryIds.add(operation.entry.id);
      return;
  }
}

/** Moves a view's clock by one round delta. */
function applyClock(view: ViewValidationIndex, operation: RoundOperation): void {
  if (operation.type === "world.started") {
    view.clock = { round: operation.roundCompleted + 1, jumping: false, tick: operation.tick };
    return;
  }
  const { clock } = view;
  if (!clock) return;
  switch (operation.type) {
    case "jump.started":
      clock.jumping = true;
      clock.round = operation.round;
      return;
    case "jump.completed":
      clock.jumping = false;
      clock.round = operation.roundCompleted + 1;
      clock.tick = operation.tick;
      return;
  }
}

/** Whether two JSON values are equal, whatever order their keys are in. */
export function sameValue(left: unknown, right: unknown): boolean {
  if (left === right) return true;
  if (typeof left !== "object" || typeof right !== "object" || left === null || right === null) {
    return false;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((item, index) => sameValue(item, right[index]))
    );
  }
  const leftEntries = Object.entries(left).filter(([, value]) => value !== undefined);
  const rightRecord = right as Record<string, unknown>;
  const rightKeys = Object.keys(rightRecord).filter(key => rightRecord[key] !== undefined);
  return (
    leftEntries.length === rightKeys.length &&
    leftEntries.every(([key, value]) => sameValue(value, rightRecord[key]))
  );
}

/** A scene as shown to a view: its id, and its events'. */
function shown(view: { sceneIds: Set<string>; eventIds: Set<string> }, scene: Scene) {
  view.sceneIds.add(scene.id);
  for (const event of scene.events) view.eventIds.add(event.id);
}

/**
 * Brings a view's things to where a write leaves them: what it places and changes, less every
 * thing with an `until` and every thing on a canvas that leaves.
 */
function applyWrite(
  things: Map<string, ThingValidationEntry>,
  written: Pick<TimelineExtension, "things" | "changes">,
): void {
  const leaving = new Set<string>();
  for (const thing of written.things ?? []) {
    things.set(thing.id, validationEntryOf(thing));
    if (thing.until !== undefined) leaving.add(thing.id);
  }
  for (const change of written.changes ?? []) {
    const entry = things.get(change.id);
    if (!entry) continue;
    if ("canvas" in change && typeof change.canvas === "string") entry.canvas = change.canvas;
    if ("name" in change && typeof change.name === "string") entry.named = true;
    if ("on" in change && typeof change.on === "object" && change.on !== null) {
      entry.on = onKind(change.on as Record<string, unknown>);
    }
    if ("styles" in change && typeof change.styles === "object" && change.styles !== null) {
      entry.styles = Object.keys(change.styles);
    }
    if ("arrangement" in change && typeof change.arrangement === "object") {
      entry.arrangement = (change.arrangement ?? undefined) as Arrangement | undefined;
    }
    if (change.until !== undefined) leaving.add(change.id);
  }
  const canvasesLeaving = new Set([...leaving].filter(id => things.get(id)?.kind === "canvas"));
  for (const [id, entry] of things) {
    if (leaving.has(id) || (entry.canvas !== undefined && canvasesLeaving.has(entry.canvas))) {
      things.delete(id);
    }
  }
}
